import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container } from '@pixi/react';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../../redux/store';
import { NUM_TOTAL_COMMUNITY_CARDS } from '../../../../types/cardTypes';
import { getHandRankProperties } from '../../../../utils/handUtils';
import { getBitmapTextDimensions } from '../../../../utils/textMetricsUtils';
import { WinningHandNameBackground } from '../../table/winningHandNameBackground';
import { WinningHandNameDisplay } from '../../table/winningHandNameDisplay';
import { createAllCardRenderProperties } from '../cardGroupUtils';
import { CommunityCardsContainer } from './communityCardsContainer';
const BACKGROUND_PADDING_WIDTH_PX = 25;
const BACKGROUND_PADDING_HEIGHT_PX = 5;
function createAllWinningHandBounds(communityCardsBounds, renderedCardHeight, nameDimensions, subNameDimensions) {
    if (!nameDimensions) {
        return;
    }
    const nameBounds = {
        x: communityCardsBounds.x,
        y: communityCardsBounds.y + renderedCardHeight / 2 + 8,
        w: nameDimensions.w,
        h: nameDimensions.h
    };
    const subNameBounds = nameDimensions && subNameDimensions
        ? {
            x: communityCardsBounds.x,
            y: nameBounds.y + nameDimensions.h / 2 + subNameDimensions.h / 2 - 2,
            w: subNameDimensions.w,
            h: subNameDimensions.h
        }
        : undefined;
    const totalTextWidth = Math.max(nameDimensions.w, subNameDimensions?.w || 0);
    const backgroundWidth = totalTextWidth + 2 * BACKGROUND_PADDING_WIDTH_PX;
    const backgroundBounds = {
        x: communityCardsBounds.x - backgroundWidth / 2,
        y: communityCardsBounds.y,
        w: backgroundWidth,
        h: (subNameBounds ? subNameBounds.y + subNameBounds.h / 2 : nameBounds.y + nameBounds.h / 2) -
            communityCardsBounds.y +
            BACKGROUND_PADDING_HEIGHT_PX
    };
    return { nameBounds, subNameBounds, backgroundBounds };
}
export const CommunityCardsGroupContainer = () => {
    const communityCardsBounds = useAppSelector((state) => state.gameDimensions.communityCardsBounds, shallowEqual);
    const handRankOrdinal = useAppSelector((state) => state.poker.currentShowdown?.handRank.handRankOrdinal);
    const kickerChart = useAppSelector((state) => state.poker.currentShowdown?.handRank.kickerChart, shallowEqual);
    const winningHandNameBackgroundColorNum = useAppSelector((state) => state.theme.themeTable?.winningHandNameBackgroundColorNum);
    const inactiveTimerBarColorNum = useAppSelector((state) => state.theme.themeTable?.inactiveTimerBarColorNum);
    const allCardRenderProperties = useMemo(() => communityCardsBounds
        ? createAllCardRenderProperties(communityCardsBounds.w, communityCardsBounds.h, NUM_TOTAL_COMMUNITY_CARDS)
        : undefined, [communityCardsBounds]);
    if (!allCardRenderProperties ||
        !communityCardsBounds ||
        winningHandNameBackgroundColorNum === undefined ||
        inactiveTimerBarColorNum === undefined) {
        return null;
    }
    const renderedCardHeight = allCardRenderProperties[0].renderedCardHeight;
    const handRankProperties = handRankOrdinal !== undefined && kickerChart ? getHandRankProperties({ handRankOrdinal, kickerChart }) : undefined;
    const nameDimensions = handRankProperties
        ? getBitmapTextDimensions(handRankProperties.name, 'winning-hand-name')
        : undefined;
    const subNameDimensions = handRankProperties?.subName
        ? getBitmapTextDimensions(handRankProperties.subName, 'winning-hand-sub-name')
        : undefined;
    const allWinningHandBounds = createAllWinningHandBounds(communityCardsBounds, renderedCardHeight, nameDimensions, subNameDimensions);
    const nameBounds = allWinningHandBounds?.nameBounds;
    const subNameBounds = allWinningHandBounds?.subNameBounds;
    const backgroundBounds = allWinningHandBounds?.backgroundBounds;
    return (_jsxs(Container, { interactiveChildren: false, children: [backgroundBounds && (_jsx(WinningHandNameBackground, { bounds: backgroundBounds, borderColorNum: inactiveTimerBarColorNum, backgroundColorNum: winningHandNameBackgroundColorNum })), _jsx(CommunityCardsContainer, { bounds: communityCardsBounds, allCardRenderProperties: allCardRenderProperties }), handRankProperties && nameBounds && (_jsx(WinningHandNameDisplay, { name: { label: handRankProperties.name, bounds: nameBounds }, subName: subNameBounds && handRankProperties?.subName
                    ? { label: handRankProperties.subName, bounds: subNameBounds }
                    : undefined }))] }));
};
